/* global fbq, ttq */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLoaderData } from "react-router-dom";
import { useCart } from "../components/CartContext";
import { FaTruckFast } from "react-icons/fa6";

import "../styles/Item.css";
import SizeGuide from "../components/SizeGuide";
import ProductImagesSlider from "../components/ProductImagesSlider";
import Popup from "../components/Popup";
import YouMayAlsoLikeSlider from "../components/YouMayAlsoLikeSlider";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

export async function loader({ params }) {
  const productId = params.id;
  const productUrl = `${apiUrl}/ct/products/${productId}`;
  const relatedProductsUrl = `${apiUrl}/ct/products/in-stock?exclude=${productId}`;

  const [productResponse, relatedProductsResponse] = await Promise.all([
    fetch(productUrl, {
      headers: { "X-App-Secret": process.env.REACT_APP_APP_SECRET },
    }),
    fetch(relatedProductsUrl, {
      headers: { "X-App-Secret": process.env.REACT_APP_APP_SECRET },
    }),
  ]);

  if (!productResponse.ok || !relatedProductsResponse.ok) {
    throw new Response(
      JSON.stringify({ message: "Could not fetch product information." }),
      {
        status: Math.max(
          productResponse.status,
          relatedProductsResponse.status
        ),
      }
    );
  }

  const product = await productResponse.json();
  const relatedProducts = await relatedProductsResponse.json();

  return { product, relatedProducts };
}

export default function Item() {
  const { product, relatedProducts } = useLoaderData();
  const { addToCart } = useCart();

  const [selectedSize, setSelectedSize] = useState(null);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupSize, setPopupSize] = useState(null);
  const [hasHovered, setHasHovered] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const contentRefs = useRef([]);

  const compositionText =
    product.composition && product.composition.length
      ? product.composition
          .map((comp) => `${comp.name} (${comp.percentage}%)`)
          .join(", ")
      : "Please contact us";

  const additionalInformation = [
    {
      title: "Product Details",
      content: {
        ul: [
          "Design: Features a classic fit that sits comfortably on the waist with eased-through-leg construction.",
          "Elastic drawstring waistband for a secure fit and a waterproof zip pocket for added practicality.",
          "CT logo on the left side",
          `Composition: ${compositionText}`,
          "Machine washable.",
          "For detailed size measurements, please refer to our size chart.",
          `Product Code: ${product.product_id}`,
        ],
      },
    },
    {
      title: "Payment Options & Delivery",
      content: {
        paymentOptions: {
          creditCards: [
            { name: "Visa", logo: "/visaLogo.svg" },
            { name: "MasterCard", logo: "/mastercardLogo.svg" },
            { name: "American Express", logo: "/amexLogo.svg" },
          ],
          otherPayments: [
            { name: "Apple Pay", logo: "/applePayLogo.svg" },
            { name: "Google Pay", logo: "/googlePayLogo.png" },
            { name: "Klarna", logo: "/klarnaLogo.svg" },
          ],
        },
      },
    },
  ];

  const toggleItem = (index) => {
    setExpandedIndex(index === expandedIndex ? null : index); // Toggle the visibility
  };

  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  const handleAddToCart = useCallback(() => {
    if (selectedSize) {
      // addToCart({ ...product, size: selectedSize }, selectedSize);
      addToCart(product.product_id, selectedSize , product.name, product.price);
      setSelectedSize(null);

      // Trigger the quick checkout panel
      const event = new CustomEvent("showCheckout");
      window.dispatchEvent(event);
    } else {
      alert("Please select a size before adding to cart.");
    }
  }, [addToCart, selectedSize, product]);

  const handleOpenPopup = (size) => {
    setPopupSize(size);
    setIsPopupOpen(true);
    setHasHovered(true);
  };

  const handleSizeClick = (size, inStock) => {
    if (inStock) {
      // If the size is in stock, select it
      setSelectedSize(size);
    } else {
      // If the size is out of stock, open the popup
      setPopupSize(size);
      setIsPopupOpen(true);
    }
  };
  useEffect(() => {
    if (isSizeGuideOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isSizeGuideOpen]);

  // useEffect(() => {
  //   if (window.fbq && product) {
  //     fbq("track", "ViewContent", {
  //       content_ids: [product.product_id], // must match the `id` in your feed
  //       content_type: "product",
  //       content_name: product.name,
  //       value: product.price,
  //       currency: "GBP",
  //     });
  //   }
  //   if (window.ttq && product) {
  //     ttq.track("ViewContent", {
  //       contents: [
  //         {
  //           content_id: product.product_id,
  //           content_type: "product",
  //           content_name: product.name,
  //         },
  //       ],
  //       value: product.price,
  //       currency: "GBP",
  //     });
  //   }
  // }, [product]);

  if (!product) {
    return <div>Product not found</div>;
  }

  const RelatedProducts = (
    <>
      <div className="related-products">
        <div className="related-products-title">
          <h2>You May Also Like</h2>
        </div>
        <div className="related-products-list">
          {isMobile ? (
            <YouMayAlsoLikeSlider products={relatedProducts} />
          ) : (
            <div className="related-products-row">
              {relatedProducts.map((relatedProduct) => (
                <a
                  href={`/shop/${relatedProduct.product_id}`}
                  className="product-link related-product-item"
                  key={relatedProduct.product_id}
                >
                  <div className="related-product-item">
                    <img
                      src={`/${relatedProduct.images[0]}`}
                      alt={relatedProduct.name}
                    />
                    <h3>{relatedProduct.name}</h3>
                    <p>£{relatedProduct.price}</p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const productJsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    image: product.images.map((img) => `${window.location.origin}/${img}`), // array of URLs
    description: product.description || "Shop now at Charles Timmins.",
    sku: product.product_id,
    offers: {
      "@type": "Offer",
      url: window.location.href,
      priceCurrency: "GBP",
      price: product.price,
      availability: product.sizes.some((size) => size.inStock)
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }}
      />
      <div className="item-container">
        {isMobile ? (
          <ProductImagesSlider
            images={product.images}
            productName={product.name}
            showPagination={true}
          />
        ) : (
          <div className="image-container">
            <div className="primary-images">
              <img src={`/${product.images[0]}`} alt={product.name} />
              <img src={`/${product.images[1]}`} alt={product.name} />
            </div>
            <div className="secondary-images">
              {product.images.slice(2).map((image, index) => (
                <div key={index} className="secondary-image">
                  <img src={`/${image}`} alt={`${product.name} ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="item-details">
          <h1>{product.name}</h1>
          <p className="price">£{product.price}</p>
          {product.description && (
            <p className="description">{product.description}</p>
          )}
          <div className="sizes">
            <p>SIZE:</p>
            <div
              className="size-guide-button"
              onClick={() => setIsSizeGuideOpen(true)}
            >
              Size Guide
            </div>
            <div className="size-options">
              {product.sizes.map(({ size, inStock }) => (
                <div
                  key={size}
                  className={`size-option ${
                    selectedSize === size ? "selected" : ""
                  } ${!inStock ? "out-of-stock" : ""}`}
                  onClick={() => handleSizeClick(size, inStock)}
                  onMouseEnter={() => {
                    if (!inStock && !hasHovered) {
                      handleOpenPopup(size);
                    }
                  }}
                >
                  {size}
                </div>
              ))}
            </div>
          </div>
          <div className="add-to-bag" onClick={handleAddToCart}>
            ADD TO CART
          </div>
          <div className="delivery-information">
            <div className="delivery-icon">
              <FaTruckFast />
            </div>
            <div className="delivery-text">
              Free standard delivery over £100 & Free Returns*
            </div>
          </div>
          <div className="additional-information-container">
            {additionalInformation.map((item, index) => (
              <div
                key={index}
                className={`additional-information-item ${
                  expandedIndex === index ? "expanded" : ""
                }`}
                onClick={() => toggleItem(index)}
              >
                <div className="additional-information-title">{item.title}</div>
                <div
                  className="additional-information-content-wrapper"
                  ref={(el) => (contentRefs.current[index] = el)}
                  style={{
                    maxHeight:
                      expandedIndex === index
                        ? contentRefs.current[index]?.scrollHeight
                        : 0,
                  }}
                >
                  <div className="additional-information-content">
                    {item.content.ul && (
                      <ul>
                        {item.content.ul.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {item.content.paymentOptions && (
                      <>
                        <h4>Credit Cards:</h4>
                        <div className="credit-cards">
                          {item.content.paymentOptions.creditCards.map(
                            (card, index) => (
                              <div key={index}>
                                <span className="img-text-wrapper">
                                  <img src={card.logo} alt={card.name} />
                                  {card.name}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                        <h4>Other Forms of Payment:</h4>
                        <div className="other-payments">
                          {item.content.paymentOptions.otherPayments.map(
                            (payment, index) => (
                              <div key={index}>
                                <span className="img-text-wrapper">
                                  <img src={payment.logo} alt={payment.name} />
                                  {payment.name}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                        <h4>
                          Standard shipping £2.50, free shipping over £100
                        </h4>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {!isMobile && RelatedProducts}
        </div>
        {isMobile && RelatedProducts}
        {/* <form className="newsletter-form" onSubmit={handleEmailSubmit}>
        <span>Join our Newsletter</span>
        <FormInput
          label="Email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          rows={1}
          required
        />
        <button type="submit" className="popup-submit">
          Subscribe
        </button>
      </form> */}
        <Popup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          title="Out of stock"
          description={`Join our newletter and waiting list to find out when ${product.name} in "${popupSize}" are back in stock`}
          productId={product.product_id}
          size={popupSize}
          reason="restock_notification"
        />
        <SizeGuide
          isMobile={isMobile}
          isOpen={isSizeGuideOpen}
          onClose={() => setIsSizeGuideOpen(false)}
        />
      </div>
    </>
  );
}
